import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import GameweekStatusBadge from "./GameweekStatusBadge";

const GameweekList = ({
  gameweeks,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onAssignMatches,
  tournamentId,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  const getStatusAction = (status) => {
    switch (status) {
      case "upcoming":
        return { icon: <PlayIcon />, label: "Open", nextStatus: "open" };
      case "open":
        return { icon: <LockIcon />, label: "Lock", nextStatus: "locked" };
      case "locked":
        return { icon: <PlayIcon />, label: "Go Live", nextStatus: "live" };
      case "live":
        return {
          icon: <CheckIcon />,
          label: "Finalise",
          nextStatus: "finalised",
        };
      case "finalised":
        return {
          icon: <ReplayIcon />,
          label: "Reopen",
          nextStatus: "upcoming",
        };
      default:
        return null;
    }
  };

  const getAssignedCount = (gameweek) => {
    return gameweek.assignedMatches || 0;
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Matches</TableCell>
            <TableCell>Date Range</TableCell>
            <TableCell>Deadline</TableCell>
            <TableCell>Budget</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {gameweeks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  No gameweeks created yet
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            gameweeks.map((gameweek) => {
              const statusAction = getStatusAction(gameweek.status);
              const assignedCount = getAssignedCount(gameweek);

              return (
                <TableRow
                  key={gameweek.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onView(gameweek.id)}
                >
                  <TableCell>{gameweek.number}</TableCell>
                  <TableCell>
                    {gameweek.name}
                    {gameweek.isDoubleGameweek && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        (DGW)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <GameweekStatusBadge status={gameweek.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {assignedCount} matches
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(gameweek.startsAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(gameweek.deadlineAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {gameweek.budgetInitial != null
                        ? `£${(gameweek.budgetInitial / 1_000_000).toFixed(1)}m`
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onView(gameweek.id);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Assign Matches">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignMatches(gameweek.id);
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(gameweek.id);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {statusAction && (
                        <Tooltip title={statusAction.label}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onStatusChange(
                                gameweek.id,
                                statusAction.nextStatus
                              );
                            }}
                          >
                            {statusAction.icon}
                          </IconButton>
                        </Tooltip>
                      )}

                      {gameweek.status === "upcoming" && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(gameweek.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GameweekList;
